'use client';
import { useLocalStorageState, useSessionStorageState } from 'ahooks';
import { type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  FEATURE_TOURS_STORAGE_KEY,
  PROCESSING_TOURS_STORAGE_KEY,
  SESSION_TOUR_COUNT_KEY,
  TOUR_FUNCTION_TIPS_LIST,
  TOUR_METADATA_CONFIG,
  type TourKeyType,
} from '@/app/constants';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { ToursStorage } from '@/app/types';
import { useTour } from './hooks/use-tour';
import { TourPopover } from './TourPopover';
import type { TourItem } from './types';

export interface FeatureTourConfig {
  /** Tour 类型（预定义的 key）或自定义 key */
  tourKey: TourKeyType;
  /** Tour 配置（UI 内容） */
  tourConfig: {
    items: TourItem[];
    modal?: boolean;
    showFooter?: boolean;
  };
}

/**
 * FeatureTour - 功能引导组件
 *
 * 特性：
 * - 负责展示和激活逻辑
 * - 队列管理由 useTourCoordinator 统一处理
 * - 当队列中有当前 Tour 且没有激活的 Tour 时，自动激活
 * - 处理 Tour 的完成状态和显示次数
 * - 会话中最多显示 2 个 Tour
 *
 * @example
 * <FeatureTour
 *   tourKey={TOUR_KEYS.EXTEND_TOUR}
 *   tourConfig={{ items: [...] }}
 * />
 */
export const FeatureTour: FC<FeatureTourConfig> = ({ tourConfig, tourKey }) => {
  // 从配置文件读取元数据
  const metadata = TOUR_METADATA_CONFIG[tourKey];

  const maxCount = metadata?.maxCount || 1;
  const disableAfterClose = metadata?.disableAfterClose;

  // 使用 tour-queue 队列系统
  const { removeFromTourQueue, activeTour, dismissActiveTour, activateNextTour, tourQueue, markTourReady } = useStore(
    (state) => ({
      removeFromTourQueue: state.removeFromTourQueue,
      activeTour: state.activeTour,
      dismissActiveTour: state.dismissActiveTour,
      activateNextTour: state.activateNextTour,
      tourQueue: state.tourQueue,
      markTourReady: state.markTourReady,
    }),
  );

  const storeApi = useStoreApi();

  // 会话级别的引导计数（全局共享）
  const [sessionTourCount, setSessionTourCount] = useSessionStorageState<number>(SESSION_TOUR_COUNT_KEY, {
    defaultValue: 0,
  });

  const [_, setCompletedTours] = useLocalStorageState<TourKeyType[]>(FEATURE_TOURS_STORAGE_KEY, {
    defaultValue: [],
    listenStorageChange: true,
  });

  const [processingTours, setProcessingTours] = useLocalStorageState<ToursStorage>(PROCESSING_TOURS_STORAGE_KEY, {
    defaultValue: {},
    listenStorageChange: true,
  });

  // 防止重复激活
  const hasActivatedRef = useRef(false);
  // 防止重复调用 onOpen
  const hasOpenedRef = useRef(false);
  // 防止重复标记 ready
  const isReadyMarkedRef = useRef(false);

  // 标记 Tour 完成
  const markTourCompleted = useCallback(
    (tourKey: TourKeyType) => {
      setCompletedTours((prev = []) => (prev.includes(tourKey) ? prev : [...prev, tourKey]));
      setProcessingTours((prev: ToursStorage = {}) => {
        const { [tourKey]: _, ...rest } = prev;
        return rest;
      });
    },
    [setCompletedTours, setProcessingTours],
  );

  // 更新进行中的 Tour
  const updateProcessingTour = useCallback(
    (tourKey: TourKeyType) => {
      const { count = 0 } = processingTours?.[tourKey] || {};
      const currentCount = count + 1;

      if (currentCount >= maxCount) {
        markTourCompleted(tourKey);
      } else {
        setProcessingTours((prev: ToursStorage = {}) => ({
          ...prev,
          [tourKey]: { count: currentCount, lastTime: Date.now() },
        }));
      }
    },
    [maxCount, processingTours, markTourCompleted, setProcessingTours],
  );

  // 记录 Tour 已读 - 使用 useRef 包装，避免依赖变化
  const markTourReadRef = useRef(() => {
    if (maxCount > 1) {
      updateProcessingTour(tourKey);
    } else {
      markTourCompleted(tourKey);
    }
  });

  // 更新 ref 的值（但不触发重新渲染）
  useEffect(() => {
    markTourReadRef.current = () => {
      if (maxCount > 1) {
        updateProcessingTour(tourKey);
      } else {
        markTourCompleted(tourKey);
      }
    };
  }, [maxCount, tourKey, updateProcessingTour, markTourCompleted]);

  // 检查当前 Tour 是否激活
  const isActive = activeTour.some((item) => item.id === tourKey);

  const handleTourClose = useCallback(
    (type?: 'manual' | 'complete') => {
      if (type === 'manual' && disableAfterClose) {
        markTourCompleted(tourKey);
      }
      // 关闭当前激活的 Tour
      dismissActiveTour(tourKey);
      // 从队列中移除
      removeFromTourQueue(tourKey);
      // 重置激活标记
      hasActivatedRef.current = false;
      // 重置打开标记
      hasOpenedRef.current = false;
    },
    [disableAfterClose, tourKey],
  );

  // 稳定的 onOpen 回调 - 使用 useCallback 但不依赖会变化的值
  const handleTourOpen = useCallback(() => {
    // 防止重复调用
    if (hasOpenedRef.current) {
      return;
    }
    hasOpenedRef.current = true;
    // 调用 ref 中的最新函数
    markTourReadRef.current();
  }, [tourKey]); // 只依赖 tourKey，不依赖其他会变化的值

  const { step, totalSteps, tourItem, anchorRef, showFooter, goToStep, closeTour, goNext, goPrev } = useTour({
    config: tourConfig,
    onOpen: handleTourOpen,
    onClose: handleTourClose,
  });

  // 提取当前 tour 在队列中的状态
  const currentTourInQueue = useMemo(() => {
    return tourQueue.find((item) => item.id === tourKey);
  }, [tourQueue, tourKey]);

  // 自动激活逻辑：组件渲染完成后，如果在队列中就标记 ready 并尝试激活
  useEffect(() => {
    // 不在队列中，跳过
    if (!currentTourInQueue) {
      return;
    }

    // 在队列中但还未标记 ready，先标记
    if (!currentTourInQueue.ready && !isReadyMarkedRef.current) {
      markTourReady(tourKey, true);
      isReadyMarkedRef.current = true;
      // 标记后会触发 tourQueue 更新，下次 effect 执行时会进入激活逻辑
      return;
    }

    // 未 ready 或已经激活过，跳过
    if (!currentTourInQueue.ready || hasActivatedRef.current) {
      return;
    }

    // 若不在功能提示列表中，检查会话限制
    if (!TOUR_FUNCTION_TIPS_LIST.includes(tourKey)) {
      // 检查会话限制
      if (sessionTourCount >= 2) {
        return;
      }
    }

    // 已经在激活列表中
    if (activeTour.some((item) => item.id === tourKey)) {
      hasActivatedRef.current = true;
      return;
    }

    // 激活
    activateNextTour(tourKey);
    setSessionTourCount((prev = 0) => prev + 1);
    hasActivatedRef.current = true;
  }, [currentTourInQueue, activeTour, sessionTourCount, tourKey]);
  // 当激活时，跳转到第一步
  useEffect(() => {
    if (isActive) {
      goToStep(0);
    } else {
      hasOpenedRef.current = false;
    }
  }, [isActive]);

  useEffect(() => {
    return () => {
      if (isReadyMarkedRef.current) {
        markTourReady(tourKey, false);
        isReadyMarkedRef.current = false;
      }

      const currentActiveTour = storeApi.getState().activeTour;
      if (currentActiveTour.some((item) => item.id === tourKey)) {
        handleTourClose('manual');
      }
    };
  }, [tourKey, markTourReady, handleTourClose, storeApi]);

  // 只有当前 Tour 被激活时才渲染
  if (!isActive) {
    return null;
  }

  return (
    <TourPopover
      open={isActive && !!tourItem}
      tourItem={tourItem}
      anchorRef={anchorRef}
      showFooter={showFooter}
      currentStep={step}
      totalSteps={totalSteps}
      onClose={() => closeTour('manual')}
      onNext={goNext}
      onPrev={goPrev}
    />
  );
};
